import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiPlayCircle, FiShield, FiClock, FiDollarSign, FiUserCheck, FiBarChart2, FiPieChart, FiTrendingUp, FiTarget } from '../utils/icons';
import Layout from '../components/layout/Layout';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import mascot from '../assets/placeholder.png';

const Home: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');
  
  // Check if user is logged in
  useEffect(() => {
    const loggedInStatus = localStorage.getItem('isLoggedIn');
    if (loggedInStatus === 'true') {
      setIsLoggedIn(true);
      try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        setUserName(userData.name || '');
      } catch (e) {
        console.error('Failed to parse user data');
      }
    }
  }, []);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          {isLoggedIn ? (
            <div className="flex flex-col lg:flex-row items-center">
              <div className="lg:w-1/2 mb-10 lg:mb-0">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                  Welcome Back, <span className="bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text">{userName}</span>
                </h1>
                <p className="text-xl text-text/70 mb-8 leading-relaxed">
                  Your financial dashboard is ready. Explore your personalized insights and recommendations.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/dashboard">
                    <Button size="lg">
                      Go to Dashboard <FiArrowRight className="ml-2" />
                    </Button>
                  </Link>
                  <Link to="/chat">
                    <Button size="lg" variant="outline">
                      Chat with Advisor
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="lg:w-1/2 relative">
                <div className="relative z-10 rounded-2xl bg-gradient-to-b from-background to-card p-1 border border-border glass-card shadow-glow">
                  <div className="bg-card rounded-xl overflow-hidden p-6 relative">
                    <div className="flex items-center mb-8">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary via-secondary to-accent p-1 relative">
                        <div className="w-full h-full rounded-full bg-background flex items-center justify-center overflow-hidden">
                          <img src={mascot} alt="Matrix Finance Mascot" className="w-10 h-10 object-contain" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-xl font-bold">Today's Summary</h3>
                        <p className="text-text/70">Financial Snapshot</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="neo-card p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-text/70">Monthly Budget</span>
                          <FiDollarSign className="text-primary" />
                        </div>
                        <p className="text-xl font-bold">$3,240.00</p>
                        <div className="text-xs text-green-500 flex items-center mt-1">
                          <FiTrendingUp className="mr-1" /> +2.4% from last month
                        </div>
                      </div>
                      
                      <div className="neo-card p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-text/70">Savings Progress</span>
                          <FiTarget className="text-accent" />
                        </div>
                        <p className="text-xl font-bold">42%</p>
                        <div className="w-full bg-card-hover rounded-full h-2 mt-2">
                          <div className="bg-accent h-2 rounded-full" style={{ width: '42%' }}></div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="neo-card p-4 mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-text/70">Recent Transactions</span>
                        <span className="text-xs text-primary">View All</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between py-1 border-b border-border">
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mr-2">
                              <FiDollarSign size={12} className="text-primary" />
                            </div>
                            <span>Grocery Store</span>
                          </div>
                          <span className="font-medium">-$84.25</span>
                        </div>
                        <div className="flex items-center justify-between py-1 border-b border-border">
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center mr-2">
                              <FiDollarSign size={12} className="text-green-500" />
                            </div>
                            <span>Salary Deposit</span>
                          </div>
                          <span className="font-medium text-green-500">+$2,450.00</span>
                        </div>
                        <div className="flex items-center justify-between py-1">
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center mr-2">
                              <FiDollarSign size={12} className="text-accent" />
                            </div>
                            <span>Restaurant</span>
                          </div>
                          <span className="font-medium">-$32.50</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Decorative elements */}
                <div className="absolute -top-10 -left-10 w-64 h-64 rounded-full bg-primary/20 blur-3xl -z-10"></div>
                <div className="absolute -bottom-10 -right-10 w-64 h-64 rounded-full bg-accent/20 blur-3xl -z-10"></div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row items-center">
              <div className="lg:w-1/2 mb-10 lg:mb-0">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                  Future of Finance with{' '}
                  <span className="bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text">
                    Matrix AI
                  </span>
                </h1>
                <p className="text-xl text-text/70 mb-8 leading-relaxed">
                  Your personal AI-powered finance advisor. Manage finances, get insights, and connect with experts in one place.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/login">
                    <Button size="lg">
                      Get Started <FiArrowRight className="ml-2" />
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button size="lg" variant="outline">
                      Learn More
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="lg:w-1/2 relative">
                <div className="relative z-10 rounded-2xl bg-gradient-to-b from-background to-card p-1 border border-border glass-card shadow-glow">
                  <div className="bg-card rounded-xl overflow-hidden p-6 relative">
                    <div className="flex items-center mb-8">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary via-secondary to-accent p-1 relative">
                        <div className="w-full h-full rounded-full bg-background flex items-center justify-center overflow-hidden">
                          <img src={mascot} alt="Matrix Finance Mascot" className="w-10 h-10 object-contain" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-xl font-bold">Matrix Finance AI</h3>
                        <p className="text-text/70">Your personal finance advisor</p>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="glass-card p-4">
                        <p className="text-text/70">How can I optimize my monthly savings?</p>
                      </div>
                      <div className="glass-card p-4 border-l-4 border-accent">
                        <p>Based on your spending patterns, you could save an additional $420 per month by:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                          <li>Reducing subscription services ($85/month)</li>
                          <li>Optimizing grocery spending ($120/month)</li>
                          <li>Adjusting entertainment budget ($215/month)</li>
                        </ul>
                        <p className="mt-2">Would you like a detailed breakdown?</p>
                      </div>
                    </div>
                    
                    <div className="mt-6 flex justify-end">
                      <div className="inline-flex space-x-2">
                        <button className="px-4 py-2 rounded-lg border border-border hover:bg-card-hover">
                          More Details
                        </button>
                        <button className="px-4 py-2 rounded-lg bg-gradient-to-r from-primary to-accent text-white">
                          Apply Suggestions
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Decorative elements */}
                <div className="absolute -top-10 -left-10 w-64 h-64 rounded-full bg-primary/20 blur-3xl -z-10"></div>
                <div className="absolute -bottom-10 -right-10 w-64 h-64 rounded-full bg-accent/20 blur-3xl -z-10"></div>
              </div>
            </div>
          )}
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20 bg-foreground">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              <span className="bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text">
                Why Choose Matrix Finance
              </span>
            </h2>
            <p className="text-xl text-text/70 max-w-2xl mx-auto">
              The most intuitive and advanced AI-powered financial assistant to help you achieve financial freedom.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AdvantageCard 
              icon={<FiShield className="text-primary" size={24} />}
              title="Secure & Private"
              description="Your financial data is encrypted and protected with the highest security standards. We never share your information."
            />
            
            <AdvantageCard 
              icon={<FiClock className="text-secondary" size={24} />}
              title="24/7 Availability"
              description="Our AI assistant is available round the clock to answer your financial queries whenever you need help."
            />
            
            <AdvantageCard 
              icon={<FiDollarSign className="text-accent" size={24} />}
              title="Cost Efficient"
              description="Save money on expensive financial advisors. Get high-quality guidance at a fraction of the cost."
            />
            
            <Link to="/financial-intelligence" className="block">
              <AdvantageCard 
                icon={<FiBarChart2 className="text-primary" size={24} />}
                title="AI-Powered Intelligence"
                description="Our advanced AI provides personalized financial advice, insights, and recommendations tailored to your financial situation."
              />
            </Link>
            
            <Link to="/consumption-insights" className="block">
              <AdvantageCard 
                icon={<FiPieChart className="text-secondary" size={24} />}
                title="Consumption Tracking"
                description="Analyze your spending habits, consumption patterns, and get personalized recommendations to optimize your finances."
              />
            </Link>
            
            <Link to="/fraud-detection" className="block">
              <AdvantageCard 
                icon={<FiShield className="text-accent" size={24} />}
                title="Fraud Detection"
                description="Our AI continuously monitors your accounts for suspicious activities and helps protect you from financial fraud."
              />
            </Link>
            
            <div className="glass-card flex flex-col items-center justify-center p-8 text-center hover:shadow-lg hover:shadow-primary/5 transition-all duration-300">
              <div className="mb-4 bg-gradient-to-br from-primary to-accent p-px rounded-full">
                <div className="w-16 h-16 rounded-full bg-foreground flex items-center justify-center">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="url(#gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15849 13.7588 7.52152 14.2151 8.06353C14.6713 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13" stroke="url(#gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 17H12.01" stroke="url(#gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <defs>
                      <linearGradient id="gradient" x1="2" y1="2" x2="22" y2="22" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#3a86ff" />
                        <stop offset="1" stopColor="#ff006e" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">Have Questions?</h3>
              <p className="text-text/70 mb-6">
                Still not sure if Matrix Finance is right for you? Our support team is ready to help.
              </p>
              <Link to="/contact">
                <Button variant="outline">Contact Us</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-background relative">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary to-accent rounded-2xl blur-lg opacity-20"></div>
            <div className="glass-card p-12 relative z-10 text-center">
              <h2 className="text-3xl font-bold mb-6">
                <span className="bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text">
                  Start Your Financial Journey Today
                </span>
              </h2>
              <p className="text-xl text-text/80 mb-8 max-w-2xl mx-auto">
                Join thousands of users who have transformed their financial future with Matrix Finance. 
                Your personal finance guide is just a click away.
              </p>
              <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <Link to="/signup">
                  <Button size="lg">
                    Get Started for Free
                  </Button>
                </Link>
                <Link to="/chat">
                  <Button size="lg" variant="secondary">
                    Try Demo Chat
                  </Button>
                </Link>
              </div>
              <p className="mt-6 text-sm text-text/50">
                No credit card required. Free plan includes basic features.
              </p>
            </div>
          </div>
        </div>
        
        {/* Background elements */}
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute -top-20 -left-20 w-80 h-80 bg-accent/10 rounded-full blur-3xl"></div>
      </section>
    </Layout>
  );
};

// Sub-components
const FeatureCard: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <Card variant="glass" className="p-4">
    <h3 className="font-medium mb-2">{title}</h3>
    <p className="text-sm text-text/70 mb-3">{description}</p>
    <Link to="/dashboard" className="text-primary text-sm flex items-center">
      Continue <FiArrowRight className="ml-1" size={14} />
    </Link>
  </Card>
);

const AdvantageCard: React.FC<{ icon: React.ReactNode; title: string; description: string }> = ({ 
  icon, title, description 
}) => (
  <Card variant="glass" className="p-6 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300">
    <div className="w-12 h-12 rounded-lg bg-card flex items-center justify-center mb-4">
      {icon}
    </div>
    <h3 className="text-xl font-semibold mb-2">{title}</h3>
    <p className="text-text/70">{description}</p>
  </Card>
);

export default Home; 